// ── Anti-Spam, Trust Score & Account Maturity System ─────────────
//
// Core principle: Verification is NOT trust.
// Trust must be earned through time, participation, and positive
// community behaviour.
//
// The trust score is NEVER visible to users.
//
// ── Trust Levels ──
//   Level 0 – New User       (0-24)   limited posting, no crowdfunds/merchant
//   Level 1 – Community      (25-99)  normal posting, can boost, create crowdfunds
//   Level 2 – Trusted        (100-249) visibility boost, reports carry weight, ambassador-eligible  ** REQUIRES VERIFICATION **
//   Level 3 – Established    (250+)    highest weight, advanced BCH Local features                  ** REQUIRES VERIFICATION **
//
// ── Verification Cap ──
//   Unverified users are capped at Level 1 regardless of score.
//   Points still accumulate, but the level won't advance past 1
//   until the user verifies. This prevents gaming the trust system
//   by simply being active without committing identity.
//
// ── Account Age Requirements (cannot be bypassed) ──
//   First 24 hours:  max 1 post
//   Days 2-7:        max 3 posts/day
//   After day 7:     normal limits
//   Crowdfund creation: 7+ days
//   Merchant listing:   7+ days
//   Merchant Ambassador: 30+ days, verified, trust 100+
//   Advanced Community:  30+ days, trust 100+
//   Trusted Merchant:    60+ days, trust 250+
//
// ── Spam Reporting Weight ──
//   Level 0:  1 point
//   Level 1:  3 points
//   Level 2:  5 points
//   Level 3: 10 points

import { isOperatorPubkey } from './platform';

// ── Trust Level Thresholds ──────────────────────────────────────

export const TRUST_LEVELS = [
  { level: 0, name: 'New User',           minScore: 0,   maxScore: 24 },
  { level: 1, name: 'Community Member',   minScore: 25,  maxScore: 99 },
  { level: 2, name: 'Trusted Member',     minScore: 100, maxScore: 249 },
  { level: 3, name: 'Established Member', minScore: 250, maxScore: Infinity },
] as const;

// ── Trust Level UI Metadata ──────────────────────────────────────
// Used by profile pages to render community standing indicators.

export const TRUST_LEVEL_UI: Record<TrustLevel, {
  name: string;
  shortName: string;
  description: string;
  color: string;       // tailwind text color
  bgColor: string;     // tailwind bg + border
  barColor: string;    // tailwind bg color for progress bar fill
  icon: string;        // emoji icon
}> = {
  0: {
    name: 'New User',
    shortName: 'New',
    description: 'Just getting started. Post, reply, and tip to build your reputation.',
    color: 'text-slate-400',
    bgColor: 'bg-slate-400/10 border-slate-400/30',
    barColor: 'bg-slate-400',
    icon: '\u{1F331}', // 🌱
  },
  1: {
    name: 'Community Member',
    shortName: 'Member',
    description: 'Active community participant with a growing track record.',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10 border-blue-500/30',
    barColor: 'bg-blue-500',
    icon: '\u{1F91D}', // \uD83E\uDD1D
  },
  2: {
    name: 'Trusted Member',
    shortName: 'Trusted',
    description: 'Consistently active, helpful community member. Reports carry extra weight.',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10 border-emerald-500/30',
    barColor: 'bg-emerald-500',
    icon: '\u{1F6E1}\uFE0F', // 🛡\uFE0F
  },
  3: {
    name: 'Established Member',
    shortName: 'Established',
    description: 'Long-standing, highly trusted community pillar.',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10 border-amber-500/30',
    barColor: 'bg-amber-500',
    icon: '\u{2B50}', // ⭐
  },
};

export type TrustLevel = 0 | 1 | 2 | 3;

// ── Verification Cap ─────────────────────────────────────────────
// Unverified users are capped at Level 1 (Community Member).
// Their score still accumulates normally, but their effective trust
// level won't exceed 1 until they verify. This makes verification
// a meaningful gate for higher trust tiers rather than just +10 points.
export const UNVERIFIED_LEVEL_CAP: TrustLevel = 1;

/**
 * Get the effective trust level for a score.
 * @param score - the raw trust score
 * @param isVerified - whether the user has verified their account.
 *   If false, the level is capped at UNVERIFIED_LEVEL_CAP (Level 1).
 *   If omitted, no cap is applied (backwards-compatible for internal use).
 */
export function getTrustLevel(score: number, isVerified?: boolean): TrustLevel {
  let level: TrustLevel;
  if (score >= 250) level = 3;
  else if (score >= 100) level = 2;
  else if (score >= 25) level = 1;
  else level = 0;

  // Cap unverified users at Level 1
  if (isVerified === false && level > UNVERIFIED_LEVEL_CAP) {
    return UNVERIFIED_LEVEL_CAP;
  }

  return level;
}

/** Get the uncapped trust level (ignores verification requirement). */
export function getRawTrustLevel(score: number): TrustLevel {
  if (score >= 250) return 3;
  if (score >= 100) return 2;
  if (score >= 25)  return 1;
  return 0;
}

export function getTrustLevelName(level: TrustLevel): string {
  return TRUST_LEVELS[level].name;
}

// ── Positive Signal Weights ─────────────────────────────────────

export const POSITIVE_SIGNALS = {
  verifiedAccount:        10,  // Has @bchnostr.com NIP-05
  perDayAccountExists:     1,  // Per day since account creation (capped at 365)
  perActiveDay:            1,  // Per day with at least one post/reaction
  perPost:                 1,  // Per post published
  perMeaningfulReply:      2,  // Per reply to another user's post
  perTipSent:              1,  // Per tip sent
  perTipReceived:          2,  // Per tip received
  perCrowdfundContribution:5,  // Per crowdfunding pledge
  perMerchantReferral:    10,  // Per successful merchant referral
  bchLocalVerifiedMerchant:25, // BCH Local verified merchant
} as const;

// ── Negative Signal Weights ─────────────────────────────────────

export const NEGATIVE_SIGNALS = {
  trustedUserSpamReport: -5,   // Spam report from Level 2+
  moderatorAction:       -10,  // Mod action (operator-issued)
  confirmedSpam:         -25,  // Confirmed spam (threshold reached)
  duplicateAccountAbuse: -25,  // Duplicate account abuse
  automatedBotBehavior:  -50,  // Automated bot behavior
} as const;

// ── Spam Report Weights by Trust Level ──────────────────────────

export const SPAM_REPORT_WEIGHT: Record<TrustLevel, number> = {
  0: 1,
  1: 3,
  2: 5,
  3: 10,
};

/** Configurable threshold at which weighted reports flag a post for moderator review */
export const SPAM_FLAG_THRESHOLD = 15;

// ── Account Age Constants ───────────────────────────────────────

const SECONDS_PER_DAY = 86400;

export const ACCOUNT_AGE = {
  /** First 24 hours: max 1 post */
  newbieHours: 24,
  /** Days 2–7: max 3 posts per day */
  restrictedDays: 7,
  /** Posts allowed in first 24h */
  newbiePostLimit: 1,
  /** Posts per day during days 2–7 */
  restrictedPostsPerDay: 3,
  /** Minimum account age for crowdfund creation */
  crowdfundMinDays: 7,
  /** Minimum account age for merchant listings */
  merchantMinDays: 7,
  /** Minimum account age for Merchant Ambassador program */
  ambassadorMinDays: 30,
  /** Minimum account age for advanced community features */
  advancedCommunityMinDays: 30,
  /** Minimum account age for Trusted Merchant status */
  trustedMerchantMinDays: 60,
} as const;

// ── Account Age Helpers ─────────────────────────────────────────

/** How many full days old is the account? */
export function accountAgeDays(firstSeenTimestamp: number): number {
  if (!firstSeenTimestamp || firstSeenTimestamp <= 0) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, Math.floor((now - firstSeenTimestamp) / SECONDS_PER_DAY));
}

/** Is the account within its first 24 hours? */
export function isNewbie(firstSeenTimestamp: number): boolean {
  return accountAgeDays(firstSeenTimestamp) < 1;
}

/** Is the account in the restricted period (days 2–7)? */
export function isRestricted(firstSeenTimestamp: number): boolean {
  const days = accountAgeDays(firstSeenTimestamp);
  return days >= 1 && days < ACCOUNT_AGE.restrictedDays;
}

/** Has the account age unlocked normal posting? (7+ days) */
export function isEstablishedAge(firstSeenTimestamp: number): boolean {
  return accountAgeDays(firstSeenTimestamp) >= ACCOUNT_AGE.restrictedDays;
}

/** Can this account create crowdfunds? (7+ days) */
export function canCreateCrowdfund(firstSeenTimestamp: number): boolean {
  return accountAgeDays(firstSeenTimestamp) >= ACCOUNT_AGE.crowdfundMinDays;
}

/** Can this account create merchant listings? (7+ days) */
export function canCreateMerchantListing(firstSeenTimestamp: number): boolean {
  return accountAgeDays(firstSeenTimestamp) >= ACCOUNT_AGE.merchantMinDays;
}

// ── Posting Rate Limit ──────────────────────────────────────────

/**
 * Given the user's first-seen timestamp and an array of their recent
 * post timestamps, returns how many more posts they can make right now.
 *
 * Returns Infinity for established accounts (7+ days).
 */
export function remainingPostsToday(
  firstSeenTimestamp: number,
  recentPostTimestamps: number[],
): number {
  // Operator is never restricted
  // (we check externally — no pubkey param here, keeps the fn pure)

  if (isEstablishedAge(firstSeenTimestamp)) return Infinity;

  const now = Math.floor(Date.now() / 1000);
  const startOfDay = now - SECONDS_PER_DAY;
  const postsToday = recentPostTimestamps.filter(ts => ts >= startOfDay).length;

  if (isNewbie(firstSeenTimestamp)) {
    return Math.max(0, ACCOUNT_AGE.newbiePostLimit - postsToday);
  }

  // Restricted period (days 2–7)
  return Math.max(0, ACCOUNT_AGE.restrictedPostsPerDay - postsToday);
}

// ── Trust Score Calculation ─────────────────────────────────────

export interface TrustSignals {
  isVerified: boolean;
  firstSeenTimestamp: number;
  activeDays: number;       // unique days with at least one post/reaction
  postCount: number;
  meaningfulReplyCount: number;
  tipsSentCount: number;
  tipsReceivedCount: number;
  crowdfundContributions: number;
  merchantReferrals: number;
  isBchLocalMerchant: boolean;
  // Negative signals
  spamReportsReceived: number;       // weighted sum from reporters
  moderatorActions: number;
  confirmedSpamCount: number;
  duplicateAccountFlags: number;
  botBehaviorFlags: number;
}

/**
 * Calculate the trust score from all gathered signals.
 * Returns a non-negative integer (floored at 0).
 */
export function calculateTrustScore(signals: TrustSignals): number {
  let score = 0;

  // ── Positive ──
  if (signals.isVerified) score += POSITIVE_SIGNALS.verifiedAccount;

  // Account age — capped at 365 days to prevent score inflation
  const ageDays = accountAgeDays(signals.firstSeenTimestamp);
  score += Math.min(ageDays, 365) * POSITIVE_SIGNALS.perDayAccountExists;

  score += Math.min(signals.activeDays, 365) * POSITIVE_SIGNALS.perActiveDay;
  score += Math.min(signals.postCount, 500) * POSITIVE_SIGNALS.perPost;
  score += Math.min(signals.meaningfulReplyCount, 500) * POSITIVE_SIGNALS.perMeaningfulReply;
  score += Math.min(signals.tipsSentCount, 200) * POSITIVE_SIGNALS.perTipSent;
  score += Math.min(signals.tipsReceivedCount, 200) * POSITIVE_SIGNALS.perTipReceived;
  score += Math.min(signals.crowdfundContributions, 50) * POSITIVE_SIGNALS.perCrowdfundContribution;
  score += Math.min(signals.merchantReferrals, 20) * POSITIVE_SIGNALS.perMerchantReferral;
  if (signals.isBchLocalMerchant) score += POSITIVE_SIGNALS.bchLocalVerifiedMerchant;

  // ── Negative ──
  score += signals.spamReportsReceived * NEGATIVE_SIGNALS.trustedUserSpamReport;
  score += signals.moderatorActions * NEGATIVE_SIGNALS.moderatorAction;
  score += signals.confirmedSpamCount * NEGATIVE_SIGNALS.confirmedSpam;
  score += signals.duplicateAccountFlags * NEGATIVE_SIGNALS.duplicateAccountAbuse;
  score += signals.botBehaviorFlags * NEGATIVE_SIGNALS.automatedBotBehavior;

  return Math.max(0, Math.floor(score));
}

// ── Feed Ranking Adjustments ────────────────────────────────────
// These are small multipliers applied to trending / feed scores.
// They nudge ranking without shadowbanning.

export const FEED_RANKING = {
  /** New accounts (Level 0) get a small penalty */
  newAccountPenalty: 0.85,
  /** Trusted (Level 2) get a slight boost */
  trustedBoost: 1.10,
  /** Established (Level 3) get the biggest boost */
  establishedBoost: 1.15,
  /** Normal accounts (Level 1) — no adjustment */
  normal: 1.0,
} as const;

export function feedRankingMultiplier(level: TrustLevel): number {
  switch (level) {
    case 0: return FEED_RANKING.newAccountPenalty;
    case 1: return FEED_RANKING.normal;
    case 2: return FEED_RANKING.trustedBoost;
    case 3: return FEED_RANKING.establishedBoost;
  }
}

// ── Eligibility Checks ──────────────────────────────────────────
// Combine trust score + account age for feature gating.

export interface EligibilityResult {
  eligible: boolean;
  reason?: string;
}

/**
 * Can purchase boosts?
 *
 * Two gates:
 *   1. The account must be VERIFIED (paid, identity-committed via NIP-05).
 *   2. The account must not be a genuinely brand-new (Level 0) account.
 *
 * IMPORTANT — why this takes the whole trust result, not just `level`:
 * the trust *score* is computed from relay data (posts, tips, age). When
 * those queries return empty — a slow/dead relay, a resumed PWA whose
 * sockets the OS killed, or simply a cold start — the score collapses to
 * just the +10 verification bonus, which is below the Level-1 threshold
 * (25). The old check keyed solely on `level`, so a legitimate verified
 * user would be told "New accounts cannot boost posts yet" purely because
 * their history hadn't loaded. That is a false positive we must never show.
 *
 * To avoid it we only hard-block a VERIFIED user when we are CONFIDENT they
 * are actually new: their account age is under a day AND they have no
 * accumulated history. Verified users past that point are always eligible,
 * even if the score query hasn't answered yet.
 */
export function canPurchaseBoost(
  trust: { level: TrustLevel; rawLevel?: TrustLevel; isVerified: boolean; ageDays?: number; score?: number },
): EligibilityResult;
/** @deprecated Pass the full trust result instead so eligibility survives sparse relay data. */
export function canPurchaseBoost(trustLevel: TrustLevel, isVerified: boolean): EligibilityResult;
export function canPurchaseBoost(
  a: TrustLevel | { level: TrustLevel; rawLevel?: TrustLevel; isVerified: boolean; ageDays?: number; score?: number },
  b?: boolean,
): EligibilityResult {
  // Normalise the two call styles into a single shape.
  const trust = typeof a === 'object'
    ? a
    : { level: a, rawLevel: a, isVerified: !!b, ageDays: undefined as number | undefined, score: undefined as number | undefined };

  if (!trust.isVerified) {
    return { eligible: false, reason: 'Verification required to boost posts. Verify your account for $1 BCH.' };
  }

  // Verified user → eligible.
  //
  // We deliberately do NOT additionally require Level 1 here. The trust
  // *level* is derived from relay-sourced signals that are frequently
  // missing at the moment of the check (dead PWA sockets, cold start, a
  // slow relay), which would collapse the level to 0 and produce the
  // false "New accounts cannot boost posts yet" block for a legitimate
  // verified user. Verification is itself the anti-spam gate for boosting:
  // it costs $1 in BCH and commits a NIP-05 identity, so a verified account
  // has already cleared a far higher bar than any score threshold.
  return { eligible: true };
}

/** Merchant Ambassador eligibility */
export function canBeMerchantAmbassador(
  trustLevel: TrustLevel, isVerified: boolean, firstSeenTimestamp: number
): EligibilityResult {
  if (!isVerified) return { eligible: false, reason: 'Verification required for the Merchant Ambassador program.' };
  if (trustLevel < 2) return { eligible: false, reason: 'Trust Score too low. Keep contributing to the community.' };
  if (accountAgeDays(firstSeenTimestamp) < ACCOUNT_AGE.ambassadorMinDays) {
    const remaining = ACCOUNT_AGE.ambassadorMinDays - accountAgeDays(firstSeenTimestamp);
    return { eligible: false, reason: `Account must be ${ACCOUNT_AGE.ambassadorMinDays}+ days old. ${remaining} day${remaining === 1 ? '' : 's'} remaining.` };
  }
  return { eligible: true };
}

/** Trusted Merchant status */
export function canBeTrustedMerchant(
  trustLevel: TrustLevel, firstSeenTimestamp: number
): EligibilityResult {
  if (trustLevel < 3) return { eligible: false, reason: 'Trust Score too low for Trusted Merchant status.' };
  if (accountAgeDays(firstSeenTimestamp) < ACCOUNT_AGE.trustedMerchantMinDays) {
    const remaining = ACCOUNT_AGE.trustedMerchantMinDays - accountAgeDays(firstSeenTimestamp);
    return { eligible: false, reason: `Account must be ${ACCOUNT_AGE.trustedMerchantMinDays}+ days old. ${remaining} day${remaining === 1 ? '' : 's'} remaining.` };
  }
  return { eligible: true };
}

// ── Operator Immunity ───────────────────────────────────────────

/** The operator is always treated as Level 3 and exempt from all restrictions. */
export function isOperator(pubkey: string): boolean {
  return isOperatorPubkey(pubkey);
}

